import { useEffect, useRef, useMemo } from 'react';
import type { NestedFieldValueType } from '@helpcenter/field-types';
import { di } from 'react-magnetic-di';
import type { FieldValues } from 'state/persisted/request-create';
import {
    useAdjustedFields,
    getSupportedFieldConfiguration,
    isFieldTypeSupported,
} from '@atlassian/ui-modifications-core/controllers';
import { REQUEST_CREATE_VIEW_TYPE } from '../../common/constants';
import type { RequestCreateViewFieldConfiguration } from '../../common/utils/configuration';
import type { FieldState } from 'state/persisted/request-create/types';

export interface Props {
    fieldValues: FieldValues;
    setFieldValue(
        field: string,
        value: string | string[] | null | NestedFieldValueType,
        shouldValidate?: boolean
    ): void;
    fields: FieldState[];
}

/**
 * `UiModificationsFormValuesApplier` applies UI Modification value overrides using the provided `setFieldValue` method and comparing with existing `fieldValues`.
 *
 * This components does not apply values visible for the user but only persists UI Modifications into the Formik state used in the RequestCreate experience.
 *
 * We assume that `setFieldValue` only accepts `string | string[] | null`
 *
 * @param props `fieldValues` and `setFieldValue` and `fields` must be retrieved from the RequestCreate Formik's render props component.
 * @returns `null` Doesn't render anything visible
 */
export const UiModificationsFormValuesApplier = ({ fieldValues, setFieldValue, fields }: Props) => {
    di(useAdjustedFields, isFieldTypeSupported, getSupportedFieldConfiguration);

    const [overrides] = useAdjustedFields();

    const fieldValuesRef = useRef(fieldValues);

    // always refresh fieldValuesRef with the latest value
    fieldValuesRef.current = fieldValues;

    const fieldConfigurations = useMemo(() => {
        const configs: Record<string, RequestCreateViewFieldConfiguration | undefined> = {};

        fields
            .filter(({ jiraFieldType }) => isFieldTypeSupported(REQUEST_CREATE_VIEW_TYPE, jiraFieldType as string))
            .forEach(({ id, jiraFieldType }) => {
                configs[id] = getSupportedFieldConfiguration<RequestCreateViewFieldConfiguration>(
                    REQUEST_CREATE_VIEW_TYPE,
                    jiraFieldType as string
                );
            });

        return configs;
    }, [fields]);

    useEffect(() => {
        if (!overrides || Object.keys(fieldConfigurations).length === 0) {
            return;
        }

        for (const [fieldId, override] of Object.entries(overrides)) {
            const fieldConfig = fieldConfigurations[fieldId];

            // Only process if field is supported, has override value, and transformation function is available
            if (fieldConfig?.modifyValueToBasic && override?.value !== undefined) {
                const transformedValue = fieldConfig.modifyValueToBasic(override.value);

                if (transformedValue !== fieldValuesRef.current[fieldId]) {
                    setFieldValue(fieldId, transformedValue);
                }
            }
        }
    }, [overrides, fieldConfigurations, setFieldValue]);

    return null;
};
